<script lang="ts">
  /* FeedbackWidget — voice-dictated UI comments. Mounted once in AppShell.

     Flow: floating talk-bubble button → crosshair targeting mode (bottom
     stripe explains, ESC/Cancel exits) → user clicks an element → viewport
     screenshot via html2canvas with the click marked → modal to dictate an
     audio comment (record / listen / re-record) and tag reasons → multipart
     POST to /t/{slug}/feedback/reports.

     Everything the widget renders carries data-feedback-ui so the screenshot
     pass can ignore it and the targeting click-catcher can let it through. */
  import { onDestroy } from "svelte";
  import { _, locale } from "svelte-i18n";
  import { loadHtml2Canvas } from "$lib/html2canvas";
  import {
    FEEDBACK_REASONS,
    buildSelector,
    submitFeedbackReport,
    type FeedbackReason,
  } from "$lib/feedback";

  let { tenantSlug }: { tenantSlug: string } = $props();

  const MAX_SECONDS = 120;

  type Mode = "idle" | "targeting" | "capturing" | "compose";
  let mode = $state<Mode>("idle");

  // What the user pointed at.
  let clickX = 0;
  let clickY = 0;
  let elementSelector = $state("");
  let elementText = "";
  let elementHtml = "";
  let screenshotBlob: Blob | null = null;
  let screenshotUrl = $state("");

  let reasons = $state<FeedbackReason[]>([]);

  // Recorder state.
  let recState = $state<"idle" | "recording" | "recorded">("idle");
  let micDenied = $state(false);
  let elapsed = $state(0);
  let audioBlob: Blob | null = null;
  let audioUrl = $state("");
  let audioDurationMs = 0;
  let recorder: MediaRecorder | null = null;
  let stream: MediaStream | null = null;
  let chunks: Blob[] = [];
  let timerId: ReturnType<typeof setInterval> | null = null;
  let recStartedAt = 0;

  let sending = $state(false);
  let sendError = $state(false);
  // Why Send did nothing: no recording yet, or one still running.
  let sendBlocked = $state<"" | "no-audio" | "recording">("");
  let toast = $state("");

  // ── Targeting ──────────────────────────────────────────────────────────

  function startTargeting() {
    mode = "targeting";
    document.documentElement.classList.add("oe-feedback-targeting");
    window.addEventListener("click", onTargetClick, true);
    window.addEventListener("keydown", onTargetKey, true);
  }

  function stopTargeting() {
    document.documentElement.classList.remove("oe-feedback-targeting");
    window.removeEventListener("click", onTargetClick, true);
    window.removeEventListener("keydown", onTargetKey, true);
  }

  function cancelTargeting() {
    stopTargeting();
    mode = "idle";
  }

  function onTargetKey(e: KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      cancelTargeting();
    }
  }

  function onTargetClick(e: MouseEvent) {
    const target = e.target as Element | null;
    // The stripe's own Cancel button must keep working normally.
    if (target?.closest?.("[data-feedback-ui]")) return;
    e.preventDefault();
    e.stopPropagation();
    clickX = e.clientX;
    clickY = e.clientY;
    elementSelector = buildSelector(target);
    elementText = (target?.textContent ?? "").trim().slice(0, 200);
    elementHtml = (target?.outerHTML ?? "").slice(0, 4000);
    stopTargeting();
    void captureAndCompose();
  }

  // ── Screenshot ─────────────────────────────────────────────────────────

  async function captureAndCompose() {
    mode = "capturing";
    screenshotBlob = null;
    screenshotUrl = "";
    try {
      // Two frames so the stripe/crosshair are gone before the render pass.
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const html2canvas = await loadHtml2Canvas();
      const canvas: HTMLCanvasElement = await html2canvas(document.body, {
        x: window.scrollX,
        y: window.scrollY,
        width: window.innerWidth,
        height: window.innerHeight,
        windowWidth: document.documentElement.clientWidth,
        windowHeight: document.documentElement.clientHeight,
        backgroundColor: "#f3f3f3",
        scale: Math.min(window.devicePixelRatio || 1, 2),
        useCORS: true,
        logging: false,
        ignoreElements: (el: Element) => el.hasAttribute?.("data-feedback-ui"),
      });
      drawClickMarker(canvas);
      screenshotBlob = await new Promise<Blob | null>((res) =>
        canvas.toBlob(res, "image/jpeg", 0.85),
      );
      if (screenshotBlob) screenshotUrl = URL.createObjectURL(screenshotBlob);
    } catch {
      // A missing screenshot must not block the comment itself (CDN down,
      // tainted canvas from cross-origin map tiles, …).
      screenshotBlob = null;
      screenshotUrl = "";
    }
    mode = "compose";
  }

  function drawClickMarker(canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const s = canvas.width / window.innerWidth;
    ctx.beginPath();
    ctx.arc(clickX * s, clickY * s, 16 * s, 0, Math.PI * 2);
    ctx.lineWidth = 3.5 * s;
    ctx.strokeStyle = "#d9534f";
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(clickX * s, clickY * s, 3.5 * s, 0, Math.PI * 2);
    ctx.fillStyle = "#d9534f";
    ctx.fill();
  }

  // ── Recording ──────────────────────────────────────────────────────────

  function pickMime(): string {
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4",
      "audio/ogg;codecs=opus",
    ];
    for (const c of candidates) {
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(c)) return c;
    }
    return "";
  }

  async function startRecording() {
    micDenied = false;
    sendBlocked = "";
    discardRecording();
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      micDenied = true;
      return;
    }
    const mime = pickMime();
    recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    chunks = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size) chunks.push(e.data);
    };
    recorder.onstop = () => {
      audioDurationMs = Date.now() - recStartedAt;
      audioBlob = new Blob(chunks, { type: recorder?.mimeType || mime || "audio/webm" });
      audioUrl = URL.createObjectURL(audioBlob);
      stream?.getTracks().forEach((t) => t.stop());
      stream = null;
      recState = "recorded";
      sendBlocked = "";
    };
    recStartedAt = Date.now();
    elapsed = 0;
    timerId = setInterval(() => {
      elapsed = Math.floor((Date.now() - recStartedAt) / 1000);
      if (elapsed >= MAX_SECONDS) stopRecording();
    }, 250);
    recorder.start();
    recState = "recording";
  }

  function stopRecording() {
    if (timerId) {
      clearInterval(timerId);
      timerId = null;
    }
    if (recorder && recorder.state !== "inactive") recorder.stop();
  }

  function discardRecording() {
    stopRecording();
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    audioUrl = "";
    audioBlob = null;
    audioDurationMs = 0;
    elapsed = 0;
    recState = "idle";
  }

  function formatClock(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  }

  // ── Compose modal ──────────────────────────────────────────────────────

  function toggleReason(r: FeedbackReason) {
    reasons = reasons.includes(r) ? reasons.filter((x) => x !== r) : [...reasons, r];
  }

  function closeCompose() {
    discardRecording();
    if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
    screenshotUrl = "";
    screenshotBlob = null;
    reasons = [];
    micDenied = false;
    sendError = false;
    sendBlocked = "";
    mode = "idle";
  }

  function onComposeKey(e: KeyboardEvent) {
    if (e.key === "Escape" && !sending) closeCompose();
  }

  async function submit() {
    if (sending) return;
    // Send stays clickable with no recording so the click can explain itself
    // instead of silently doing nothing.
    if (!audioBlob) {
      sendBlocked = recState === "recording" ? "recording" : "no-audio";
      return;
    }
    sending = true;
    sendError = false;
    sendBlocked = "";
    try {
      await submitFeedbackReport(tenantSlug, {
        audio: audioBlob,
        audioDurationMs,
        screenshot: screenshotBlob,
        pageUrl: window.location.href,
        clickX,
        clickY,
        viewportW: window.innerWidth,
        viewportH: window.innerHeight,
        elementSelector,
        elementText,
        elementHtml,
        reasons,
        language: $locale ?? "",
      });
      closeCompose();
      toast = $_("feedback.sent");
      setTimeout(() => (toast = ""), 3000);
    } catch {
      sendError = true;
    } finally {
      sending = false;
    }
  }

  onDestroy(() => {
    stopTargeting();
    discardRecording();
    if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
  });
</script>

<svelte:window onkeydown={mode === "compose" ? onComposeKey : undefined} />

{#if mode === "idle"}
  <div class="fb-fab-wrap" data-feedback-ui>
    <button
      type="button"
      class="fb-fab"
      aria-label={$_("feedback.fab.title")}
      aria-describedby="fb-fab-tip"
      onclick={startTargeting}
    >
      <span class="material-icons-outlined" aria-hidden="true">feedback</span>
    </button>
    <div class="fb-tip" id="fb-fab-tip" role="tooltip">
      <strong>{$_("feedback.fab.title")}</strong>
      <span>{$_("feedback.fab.tooltip")}</span>
    </div>
  </div>
{/if}

{#if mode === "targeting"}
  <div class="fb-stripe" data-feedback-ui role="status">
    <span class="material-icons-outlined" aria-hidden="true">ads_click</span>
    <span class="fb-stripe-text">{$_("feedback.stripe.hint")}</span>
    <button type="button" class="fb-stripe-cancel" onclick={cancelTargeting}>
      {$_("common.cancel")}
    </button>
  </div>
{/if}

{#if mode === "capturing"}
  <div class="fb-capturing" data-feedback-ui aria-hidden="true">
    <span class="fb-spinner"></span>
  </div>
{/if}

{#if mode === "compose"}
  <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
  <div class="fb-backdrop" data-feedback-ui role="presentation" onclick={() => !sending && closeCompose()}>
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="fb-modal" role="dialog" aria-modal="true" tabindex="-1" onclick={(e) => e.stopPropagation()}>
      <div class="fb-header">
        <span class="material-icons-outlined fb-header-icon" aria-hidden="true">feedback</span>
        <h2 class="fb-title">{$_("feedback.modal.title")}</h2>
        <button
          type="button"
          class="fb-x"
          onclick={closeCompose}
          disabled={sending}
          aria-label={$_("common.close")}
        >
          <span class="material-icons-outlined">close</span>
        </button>
      </div>

      <div class="fb-body">
        {#if screenshotUrl}
          <img class="fb-shot" src={screenshotUrl} alt="" />
        {/if}
        {#if elementSelector}
          <div class="fb-element" title={elementSelector}>
            <span class="material-icons-outlined" aria-hidden="true">my_location</span>
            <code>{elementSelector}</code>
          </div>
        {/if}

        <div class="fb-reasons" role="group" aria-label={$_("feedback.reasons.label")}>
          <span class="fb-reasons-label">{$_("feedback.reasons.label")}</span>
          <div class="fb-pills">
            {#each FEEDBACK_REASONS as r (r)}
              <button
                type="button"
                class="fb-pill"
                class:selected={reasons.includes(r)}
                aria-pressed={reasons.includes(r)}
                onclick={() => toggleReason(r)}
              >
                {$_(`feedback.reason.${r}`)}
              </button>
            {/each}
          </div>
        </div>

        <div class="fb-recorder">
          {#if recState === "idle"}
            <p class="fb-rec-hint">{$_("feedback.record.hint")}</p>
            {#if micDenied}
              <p class="fb-mic-denied">{$_("feedback.mic.denied")}</p>
            {/if}
            <button type="button" class="fb-rec-btn" onclick={startRecording}>
              <span class="material-icons-outlined" aria-hidden="true">mic</span>
              {$_("feedback.record.start")}
            </button>
          {:else if recState === "recording"}
            <div class="fb-rec-live">
              <span class="fb-rec-dot" aria-hidden="true"></span>
              <span class="fb-rec-clock">{formatClock(elapsed)} / {formatClock(MAX_SECONDS)}</span>
            </div>
            <button type="button" class="fb-rec-btn fb-rec-btn--stop" onclick={stopRecording}>
              <span class="material-icons-outlined" aria-hidden="true">stop</span>
              {$_("feedback.record.stop")}
            </button>
          {:else}
            <audio class="fb-audio" controls src={audioUrl}></audio>
            <button type="button" class="fb-rec-again" onclick={startRecording}>
              <span class="material-icons-outlined" aria-hidden="true">restart_alt</span>
              {$_("feedback.record.again")}
            </button>
          {/if}
        </div>

        {#if sendBlocked}
          <p class="fb-send-blocked" role="alert">
            <span class="material-icons-outlined" aria-hidden="true">info</span>
            {sendBlocked === "recording"
              ? $_("feedback.record.stopFirst")
              : $_("feedback.record.required")}
          </p>
        {/if}

        {#if sendError}
          <p class="fb-send-error">{$_("feedback.error")}</p>
        {/if}
      </div>

      <div class="fb-footer">
        <button type="button" class="btn btn--ghost" onclick={closeCompose} disabled={sending}>
          {$_("common.cancel")}
        </button>
        <button
          type="button"
          class="btn btn--primary"
          class:fb-send--waiting={recState !== "recorded"}
          onclick={submit}
          disabled={sending}
        >
          {sending ? $_("feedback.sending") : $_("feedback.send")}
        </button>
      </div>
    </div>
  </div>
{/if}

{#if toast}
  <div class="fb-toast" data-feedback-ui role="status">{toast}</div>
{/if}

<style>
  :global(html.oe-feedback-targeting),
  :global(html.oe-feedback-targeting *) {
    cursor: crosshair !important;
  }

  .fb-fab-wrap {
    position: fixed;
    right: 20px;
    bottom: 20px;
    z-index: 1600;
  }

  .fb-fab {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: none;
    background: var(--color-primary);
    color: var(--color-text-on-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: var(--shadow-pop);
    transition: background 0.12s, transform 0.12s;
  }
  .fb-fab:hover {
    background: var(--color-primary-hover);
    transform: scale(1.06);
  }
  .fb-fab .material-icons-outlined {
    font-size: 24px;
  }

  /* Hover/focus tooltip — the FAB is otherwise an unlabelled blue circle. */
  .fb-tip {
    position: absolute;
    right: calc(100% + 12px);
    bottom: 2px;
    width: max-content;
    max-width: 260px;
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 10px 12px;
    border-radius: var(--radius);
    background: var(--color-surface-darker);
    color: var(--color-text-on-dark);
    box-shadow: var(--shadow-pop);
    font-size: var(--fs-13);
    line-height: 1.35;
    text-align: left;
    opacity: 0;
    transform: translateX(6px);
    pointer-events: none;
    transition: opacity 0.12s ease, transform 0.12s ease;
  }
  .fb-tip strong {
    font-weight: 700;
  }
  .fb-tip span {
    color: rgba(255, 255, 255, 0.78);
    font-size: var(--fs-12);
  }
  .fb-tip::after {
    content: "";
    position: absolute;
    top: 18px;
    left: 100%;
    border: 6px solid transparent;
    border-left-color: var(--color-surface-darker);
  }
  .fb-fab-wrap:hover .fb-tip,
  .fb-fab:focus-visible + .fb-tip {
    opacity: 1;
    transform: translateX(0);
  }
  @media (max-width: 600px) {
    .fb-tip {
      display: none;
    }
  }

  .fb-stripe {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 2300;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--sp-3);
    padding: 12px 16px;
    background: var(--color-surface-darker);
    color: var(--color-text-on-dark);
    font-size: var(--fs-14);
    box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.25);
    cursor: default !important;
  }
  .fb-stripe .material-icons-outlined {
    color: var(--color-primary);
    font-size: 20px;
  }
  .fb-stripe-cancel {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.25);
    color: #fff;
    border-radius: var(--radius);
    padding: 6px 14px;
    font-family: inherit;
    font-size: var(--fs-13);
    font-weight: 600;
    cursor: pointer !important;
  }
  .fb-stripe-cancel:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  .fb-capturing {
    position: fixed;
    inset: 0;
    z-index: 2300;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.25);
  }
  .fb-spinner {
    width: 34px;
    height: 34px;
    border: 3px solid var(--color-primary-soft);
    border-top-color: var(--color-primary);
    border-radius: 50%;
    animation: fb-spin 0.8s linear infinite;
  }
  @keyframes fb-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .fb-backdrop {
    position: fixed;
    inset: 0;
    z-index: 2300;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
  }
  .fb-modal {
    background: #fff;
    border-radius: var(--radius-lg);
    width: 100%;
    max-width: 520px;
    max-height: calc(100vh - 40px);
    box-shadow: 0 24px 72px rgba(0, 0, 0, 0.28);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .fb-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 18px 22px 12px;
    border-bottom: 1px solid var(--color-divider);
  }
  .fb-header-icon {
    color: var(--color-primary);
    font-size: 22px;
  }
  .fb-title {
    margin: 0;
    font-size: var(--fs-18);
    font-weight: 700;
    color: var(--color-text);
    flex: 1;
  }
  .fb-x {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--color-text-muted);
    display: flex;
    padding: 2px;
  }
  .fb-x:hover {
    color: var(--color-text);
  }

  .fb-body {
    padding: 16px 22px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    overflow-y: auto;
  }
  .fb-shot {
    width: 100%;
    border: 1px solid var(--color-border);
    border-radius: var(--radius);
    max-height: 200px;
    object-fit: contain;
    background: var(--color-bg);
  }
  .fb-element {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--fs-12);
    color: var(--color-text-muted);
    min-width: 0;
  }
  .fb-element .material-icons-outlined {
    font-size: 16px;
    flex-shrink: 0;
  }
  .fb-element code {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .fb-reasons {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .fb-reasons-label {
    font-size: var(--fs-13);
    font-weight: 600;
    color: var(--color-text);
  }
  .fb-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .fb-pill {
    border: 1px solid var(--color-border);
    background: #fff;
    color: var(--color-text-muted);
    border-radius: var(--radius-pill);
    padding: 6px 14px;
    font-family: inherit;
    font-size: var(--fs-13);
    font-weight: 600;
    cursor: pointer;
    transition: background 0.12s, color 0.12s, border-color 0.12s;
  }
  .fb-pill:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }
  .fb-pill.selected {
    background: var(--color-primary-soft);
    border-color: var(--color-primary);
    color: var(--color-primary);
  }

  .fb-recorder {
    border: 1px dashed var(--color-border);
    border-radius: var(--radius);
    padding: 14px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    background: #fafbfc;
  }
  .fb-rec-hint {
    margin: 0;
    font-size: var(--fs-13);
    color: var(--color-text-muted);
    text-align: center;
  }
  .fb-mic-denied {
    margin: 0;
    font-size: var(--fs-13);
    color: var(--color-danger);
    text-align: center;
  }
  .fb-rec-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: none;
    border-radius: var(--radius-pill);
    padding: 10px 20px;
    background: var(--color-danger);
    color: #fff;
    font-family: inherit;
    font-size: var(--fs-14);
    font-weight: 600;
    cursor: pointer;
  }
  .fb-rec-btn:hover {
    filter: brightness(0.95);
  }
  .fb-rec-btn--stop {
    background: var(--color-surface-dark);
  }
  .fb-rec-live {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .fb-rec-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--color-danger);
    animation: fb-blink 1s ease-in-out infinite;
  }
  @keyframes fb-blink {
    50% {
      opacity: 0.25;
    }
  }
  .fb-rec-clock {
    font-variant-numeric: tabular-nums;
    font-size: var(--fs-14);
    color: var(--color-text);
    font-weight: 600;
  }
  .fb-audio {
    width: 100%;
  }
  .fb-rec-again {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    color: var(--color-primary);
    font-family: inherit;
    font-size: var(--fs-13);
    font-weight: 600;
    cursor: pointer;
    padding: 4px 8px;
  }
  .fb-rec-again .material-icons-outlined {
    font-size: 18px;
  }
  .fb-send-error {
    margin: 0;
    font-size: var(--fs-13);
    color: var(--color-danger);
  }
  .fb-send-blocked {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border: 1px solid var(--color-primary);
    border-radius: var(--radius);
    background: var(--color-primary-soft);
    color: var(--color-primary);
    font-size: var(--fs-13);
    font-weight: 600;
  }
  .fb-send-blocked .material-icons-outlined {
    font-size: 18px;
    flex-shrink: 0;
  }

  /* Clickable but not yet sendable — the click explains what's missing. */
  .fb-send--waiting {
    opacity: 0.65;
  }

  .fb-footer {
    padding: 12px 22px;
    border-top: 1px solid var(--color-divider);
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    background: #fafbfc;
  }

  .fb-toast {
    position: fixed;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 2400;
    background: var(--color-surface-darker);
    color: #fff;
    padding: 10px 18px;
    border-radius: var(--radius);
    font-size: var(--fs-14);
    box-shadow: var(--shadow-pop);
  }
</style>
